import type { ReactNode } from 'react';

import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense, useMemo } from 'react';

import { ElectricalProjectContext } from '../../hooks/use-project';
import { elektraAppQueryOptions } from '../../queries/query-options';
import { electricalProjectSchema } from '../../validators/electrical-project';
import { ProjectNotFound } from '../project-not-found';

export type ElectricalProjectProviderProps = {
    children: ReactNode;
    electricalProjectVersionId: string;
    fallback?: ReactNode;
};

export function ElectricalProjectProviderInner({
    children,
    electricalProjectVersionId,
}: ElectricalProjectProviderProps) {
    const projectQuery = useSuspenseQuery({
        ...elektraAppQueryOptions.project.edit({ electricalProjectVersionId }),
        queryFn: async (context) => {
            try {
                const { Project } = await elektraAppQueryOptions.project.edit({ electricalProjectVersionId }).queryFn(context);

                if (!Project) {
                    return { error: 'Project not found' };
                }

                return { Project };
            }
            catch (error) {
                console.error(error);
                return { error: 'Failed to fetch project' };
            }
        },
    });
    const templateQuery = useSuspenseQuery({
        ...elektraAppQueryOptions.project.request.inspectionReportsListOptions({ electricalProjectVersionId }),
        queryFn: async (context) => {
            try {
                const result = await elektraAppQueryOptions.project.request.inspectionReportsListOptions({ electricalProjectVersionId }).queryFn(context);

                return { Templates: result.Templates ?? [], Count: result.Count ?? 0 };
            }
            catch (error) {
                console.error(error);
                return { Templates: [], Count: 0, error: 'Failed to fetch templates' };
            }
        },
    });

    const project = useMemo(() => {
        const project = projectQuery.data?.Project ?? null;
        const templates = templateQuery.data?.Templates?.filter(x => x.Selected) ?? [];
        const templateInspectionKinds = templates?.map(x => x.ElectricalInspectionKind) ?? [];

        if (!project) {
            return null;
        }

        const selectedInspectionKinds = new Set(templateInspectionKinds.filter((kind): kind is NonNullable<typeof kind> => kind != null));

        const result = electricalProjectSchema.safeParse({
            electricalProjectVersionId: project.ElectricalProjectVersionId,
            enabledElectricalProjectPriceQuote: project.EnabledElectricalProjectPriceQuote,
            enabledElectricalProjectPlanning: project.EnabledElectricalProjectPlanning,
            enabledElectricalProjectRecommendations: project.EnabledElectricalProjectRecommendations,
            isCombinationInspection: project?.IsCombinationInspection ?? false,
            selectedTemplates: templates.map(x => ({
                electricalProjectDocumentTemplateId: x.ElectricalProjectDocumentTemplateId,
                inspectionKind: x.ElectricalInspectionKind,
            })) ?? [],
            selectedInspectionKinds,
        });

        if (!result.success) {
            console.error(result.error);
            return null;
        }

        return result.data;
    }, [projectQuery, templateQuery]);

    if (!project) {
        return <ProjectNotFound />;
    }

    return (
        <>
            <ElectricalProjectContext.Provider value={project}>
                {children}
            </ElectricalProjectContext.Provider>
        </>
    );
}

export function ElectricalProjectProvider(props: ElectricalProjectProviderProps) {
    return (
        <Suspense fallback={props.fallback ?? null}>
            <ElectricalProjectProviderInner {...props} />
        </Suspense>
    )
}
